import LoadingCard from "@components/card/LoadingCard.component";
import { PipelineNode, useNodesThatNeedBuilding } from "@models/pipelineNode";
import { summarizeNumber } from "@services/formatting.service";
import { timeAgo } from "@services/time.service";
import { useMissionControlDataFlowData, usePipelineNodes } from "@stores/data.store";
import { ReactNode, useCallback, useMemo, useState } from "react";
import { Badge } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import styled, { css } from "styled-components";
import PipelineNodeIcon, { DashboardIcon } from "./PipelineNodeIcon.component";
import { ReportBuilderMeasure, useMeasures } from "@models/reportBuilder";
import { getGroupValueForNodeType, getNodeTypeConfig } from "@services/modeling.service";
import { DraftOnly } from "@components/project/DraftModeRequired.component";
import { getMetricErrors } from "@services/metricValidation.service";
import { Analysis } from "@models/analysis";
import { Dashboard } from "@models/dashboard";
import DAG from "@components/datamodel/Dag.component";
import ObjectListHeader from "@components/common/ObjectListHeader.component";
import { useValidationErrorsForNode } from "@services/nodeValidation.service";


export const LibraryStyles = styled.div`
    background-color: white;

    h2 {
        font-family: Poppins;
        font-size: 14px;
        font-weight: 600;
        line-height: 21px;
        letter-spacing: 0em;
        text-align: left;
        margin-bottom: .7em;
    }


    
`

export const LibraryItemStyles = styled.div<{compact?: boolean, active?: boolean, rounded?: boolean, topBorder?: string}>`
    display: flex;
    padding: 1rem;
    cursor: pointer;
    border: 1px solid #e5e5e5;
    border-radius: none;
    min-width: 0; // Important for text truncation in flex containers

    ${props => props.compact && css`
        padding: .5rem;
    `}

    ${props => props.rounded && css`
        border-radius: 0.5rem !important;
    `}

    ${props => props.topBorder && css`
        border-top: 5px solid var(--pliable-${props.topBorder});
    `}

    background: white;
    
    &:hover {
        background: #f8f9fa;
    }
    
    &.active {
        background: #e9ecef;
    }
    
    h4 {
        margin: 0;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 100%; // Ensures the text doesn't overflow its container
    }
`;

interface SingleMeasureItemProps {
    measure: ReportBuilderMeasure;
    onClick?: () => void;
    compact?: boolean;
    rounded?: boolean;
}
export const SingleMeasureItem = (props: SingleMeasureItemProps) => {
    const errors = getMetricErrors(props.measure);
    const firstError = errors.length > 0 ? errors[0] : undefined;

    return <LibraryItem 
        topBorder="green" 
        description={props.measure.description || 'No description'} 
        rounded={props.rounded} 
        compact={props.compact} 
        itemType="measure" 
        name={props.measure.name}
        error={firstError}
        onClick={() => {
            if (props.onClick) {
                props.onClick();
            }
        }}
    >
        <DashboardIcon icon="mdi mdi-pound-box" bgColor="success" compact={props.compact}/>
    </LibraryItem>
}

interface SingleDashboardItemProps {
    dashboard: Dashboard;
    onClick?: () => void;
    compact?: boolean;
    rounded?: boolean;
}

export const SingleDashboardItem = (props: SingleDashboardItemProps) => {
    return <LibraryItem 
        topBorder="blue" 
        description={props.dashboard.description || 'No description'} 
        rounded={props.rounded} 
        compact={props.compact} 
        itemType="dashboard" 
        name={props.dashboard.name}
        onClick={() => {
            if (props.onClick) {
                props.onClick();
            }
        }}
    >
        <DashboardIcon icon="mdi mdi-view-dashboard" bgColor="blue" compact={props.compact}/>
    </LibraryItem>
}

interface SingleAnalysisItemProps {
    analysis: Analysis;
    onClick?: () => void;
    compact?: boolean;
    rounded?: boolean;
}

export const getAnalysisIcon = (visualizationType: string) => {
    if (!visualizationType) {
        return 'mdi mdi-chart-box';
    }
    switch (visualizationType) {
        case 'BAR':
            return 'mdi mdi-chart-bar';
        case 'LINE':
            return 'mdi mdi-chart-line';
        case 'TABLE':
            return 'mdi mdi-table';
        case 'PIVOT_TABLE':
            return 'mdi mdi-table-pivot';
        case 'SINGLE_VALUE':
            return 'mdi mdi-numeric';
        default:
            return visualizationType.startsWith('PREMIUM_') 
                ? 'mdi mdi-star' 
                : 'mdi mdi-chart-box';
    }
};

export const SingleAnalysisItem = (props: SingleAnalysisItemProps) => {

    return <LibraryItem 
        topBorder="pink" 
        description={props.analysis.description || 'No description'} 
        rounded={props.rounded} 
        compact={props.compact} 
        itemType="analysis" 
        name={props.analysis.name}
        onClick={() => {
            if (props.onClick) {
                props.onClick();
            }
        }}
        
    >
        <DashboardIcon 
            icon={getAnalysisIcon(props.analysis.visualization_type)} 
            bgColor="pink" 
            compact={props.compact}
        />
    </LibraryItem>
}

interface SingleNodeItemProps {
    node: PipelineNode;
    onClick?: () => void;
    compact?: boolean;
    rounded?: boolean;
    active?: boolean;
}



export const SingleNodeItem = (props: SingleNodeItemProps) => {

    const conf = useMemo(() => {
        return getNodeTypeConfig(props.node);
    }, [props.node]);

    return <LibraryItem topBorder={conf.group?.color} active={props.active} rounded={props.rounded} compact={props.compact} itemType={props.node.node_type} name={props.node.label} totalRecords={props.node.total_records} lastBuild={props.node.last_build_completed} onClick={() => {
        if (props.onClick) {
            props.onClick();
        }
    }}>
        <PipelineNodeIcon node={props.node} compact={props.compact}/>
    </LibraryItem>
}


export interface LibraryItemProps {
    itemType: string;
    children?: ReactNode;
    totalRecords?: number;
    lastBuild?: Date;
    name: string;
    description?: string;
    onClick: () => void;
    compact?: boolean;
    rounded?: boolean;
    active?: boolean;
    topBorder?: string;
    error?: string;
    className?: string;
    buttons?: ReactNode;
}

const TruncatedDescription = styled.div`
    color: var(--brand-gray-2);
    font-size: 13px;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    max-height: 2.6em;
    line-height: 1.3;
`;

export const LibraryItem = (props: LibraryItemProps) => {
    return (
        <LibraryItemStyles compact={props.compact} topBorder={props.topBorder} rounded={props.rounded} active={props.active} className={`list-group-item library-item d-flex ${props.className}`} onClick={props.onClick}>
            <div className="d-flex flex-row align-items-center">
                {props.children}
            </div> 
            <div className="d-flex flex-column px-2 justify-content-center flex-grow-1" style={{ minWidth: 0 }}> {/* Added minWidth: 0 */}
                {props.compact && <>
                    <h4 className="mb-0 font-13">{props.name}</h4>
                    {props.totalRecords !== undefined && <span className="text-muted font-13">
                        <i className="mdi mdi-file-multiple"></i> {summarizeNumber(props.totalRecords || 0)} records
                    </span>}
                </>}
                {!props.compact && <>
                    <h4>{props.name}</h4>
                    {props.description && <TruncatedDescription>{props.description}</TruncatedDescription>}
                    <div className="text-muted font-13">
                        {props.totalRecords !== undefined && <span className="me-3">
                            <i className="mdi mdi-file-multiple"></i> {summarizeNumber(props.totalRecords || 0)} records
                        </span>}
                        {props.lastBuild !== undefined && <span>
                            <i className="mdi mdi-clock"></i> {props.lastBuild ? timeAgo(props.lastBuild) : 'Never Built'}
                        </span>}
                    </div>
                </>}
            </div>
            {props.error && (
                <div className="d-flex align-items-center" onClick={(e) => e.stopPropagation()}>
                    <i title={props.error} className="mdi mdi-alert-circle text-danger font-18" style={{ cursor: 'pointer' }}></i>

                </div>
            )}
            {props.buttons}
        </LibraryItemStyles>
    );
} 
interface NodeListProps {
    nodes: PipelineNode[];
    compact?: boolean;
    onClick?: (node: PipelineNode) => void;
    selectedNodeId?: string;
}

export const NodeList = (props: NodeListProps) => {
    const navigate = useNavigate();
    const nodes = usePipelineNodes();
    const isLoading = nodes.isLoading;

    if (isLoading) {
        return <LoadingCard />;
    }
    
    return (
        <LibraryStyles>
            <div className="list-group">
                {props.nodes.map((n) => {
                    if (!n) {
                        return null;
                    }

                    if (n.node_type === 'MEASURE') {
                        return (
                            <SingleMeasureItem
                                key={n.id}
                                measure={n as unknown as ReportBuilderMeasure}
                                compact={props.compact}
                                onClick={() => {
                                    props.onClick && props.onClick(n);
                                }}
                            />
                        );
                    }

                    if (n.node_type === 'ANALYSIS') {
                        return (
                            <SingleAnalysisItem
                                key={n.id}
                                analysis={n as unknown as Analysis}
                                compact={props.compact}
                                onClick={() => {
                                    props.onClick && props.onClick(n);
                                }}
                            />
                        );
                    }

                    return (
                        <SingleNodeItem
                            key={n.id}
                            compact={props.compact}
                            node={n}
                            active={props.selectedNodeId == n.id}
                            onClick={() => {
                                props.onClick && props.onClick(n);
                                if (!props.onClick) {
                                    navigate(`/node/${n.id}`);
                                }
                            }}
                        />
                    );
                })}
            </div>
        </LibraryStyles>
    );
};


interface NodeFilterProps {
    nodeFilter?: (node: PipelineNode) => boolean;
    searchQuery?: string;
}

interface PipelineNodeListRowProps {
    node: PipelineNode;
    onViewConnections: () => void;
}

const PipelineNodeListRow = (props: PipelineNodeListRowProps) => {
    const node = props.node;
    const validationErrors = useValidationErrorsForNode(props.node);
    const nodesThatNeedBuilding = useNodesThatNeedBuilding();
    const nodeStatus = useMemo(() => {
        if (validationErrors.length > 0) {
            const hasAtLeastOneError = validationErrors.some(e => e.alertType == 'error');
            if (!hasAtLeastOneError) {
                return 'warning';
            }
            return 'error';
        }

        if (nodesThatNeedBuilding.data && nodesThatNeedBuilding.data.includes(node.id as string)) {
            return 'needs_building';
        }
        return 'ok';
    }, [validationErrors]);
    return <tr key={node.id}>
        <td>
            <Link to={`/node/${node.id}`}>{node.label}</Link>
        </td>
        <td>{summarizeNumber(node.total_records || 0)}</td>
        <td>{node.last_build_completed ? timeAgo(node.last_build_completed) : 'Never'}</td>
        <td>
            {nodeStatus == 'needs_building' && <Badge bg="warning">Needs Build</Badge>}
            {nodeStatus == 'error' && <Badge bg="danger">Error</Badge>}
            {nodeStatus == 'warning' && <Badge bg="warning">Warning</Badge>}
            {nodeStatus == 'ok' && <Badge bg="success">Up to date</Badge>}
        </td>
        <td>
            <Link to={`/node/${node.id}`} className="btn btn-light btn-sm me-1"><i className="mdi mdi-table"></i> Data</Link>
            <Link to={`/node/${node.id}/config`} className="btn btn-light btn-sm me-1"><i className="mdi mdi-cog"></i> Configure</Link>
            <button className="btn btn-light btn-sm" onClick={props.onViewConnections}>
                <i className="mdi mdi-transit-connection-variant"></i> Connections
            </button>
        </td>
    </tr>;
}

interface PipelineNodeListProps extends NodeFilterProps {
    headerIconColor: string;
    headerIcon: string;
    title: string;
    actionButtonText: string;
    actionButtonIcon: string;
    actionButtonOnClick: () => any;
    headerChildren?: ReactNode;
    plural?: string;
}


const PipelineNodeList = (props: PipelineNodeListProps) => {
    const nodes = usePipelineNodes();
    const nodesThatNeedBuilding = useNodesThatNeedBuilding();
    


    const nodesById: {[key: string]: PipelineNode} = useMemo(() => {
        if (!nodes.data) {
            return {}
        }

        const theMap: {[key: string]: PipelineNode} = {};
        nodes.data.forEach((n) => {
            if (!n) {
                return;
            }
            theMap[n.id as string] = n;
        });
        return theMap;
    }, [nodes.dataUpdatedAt]);

    const sortedNodes = useMemo(() => {
        if (!nodes.data) {
            return [];
        }

        return nodes.data.filter(n => {
            if (props.nodeFilter && !props.nodeFilter(n)) {
                return false;
            }
            return n.label.toLowerCase().indexOf(props.searchQuery?.toLowerCase() || '') >= 0;
        }).sort((a, b) => {
            return a.name.localeCompare(b.name);
        });
    }, [nodes.dataUpdatedAt, props.searchQuery]);

    const [showDag, setShowDag] = useState(false);
    const [dagRootNode, setDagRootNode] = useState<PipelineNode|undefined>(undefined);
    const [dagMode, setDagMode] = useState<'UPSTREAM' | 'DOWNSTREAM' | 'ALL'>('ALL');

    const getNodeStatus = useCallback((node: PipelineNode) => {
        
        
        if (node.build_status == 'success') {
            return 'Up to date';
        }
        if (node.build_status == 'error') {
            return 'Build Error';
        }
        if (node.build_status == 'queued') {
            return 'Build Queued';
        }
        if (!node.last_build_completed) {
            return 'Never Built';
        }
        return 'Unknown';
    }, [nodesThatNeedBuilding.dataUpdatedAt]);

    const listContent = () => {
        if (nodes.isLoading) {
            return <LoadingCard/>
        }
    
        if (!nodes.isLoading && !sortedNodes.length && !props.searchQuery) {
            return <div className="empty-state p-3">
                <div className="text-center">
                    <h3>No nodes found.</h3>
                </div>
            </div>
        }
    
        if (!nodes.isLoading && !sortedNodes.length && !!props.searchQuery) {
            return <div className="empty-state p-3">
                <div className="text-center">
                    <h3>No matching nodes found.</h3>
                </div>
            </div>
        }
    
        return <>
            <DAG
                show={showDag}
                mode={dagMode}
                rootNodeId={dagRootNode ? 'PipelineNode:' + dagRootNode.id as string : ''}
                onHide={() => setShowDag(false)}
                nodeTitle={dagRootNode?.label}
            />
            <table className="table table-bordered table-centered table-fixed mb-5">
                <thead className="bg-light">
                    <tr>
                        <th style={{width: '20%'}}>Name</th>
                        <th style={{width: '10%'}}>Records</th>
                        <th style={{width: '20%'}}>Last Updated</th>
                        <th style={{width: '10%'}}>Status</th>
                        <th style={{width: '30%'}}>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {sortedNodes.map(node => {
                        return <PipelineNodeListRow node={node} key={node.id} onViewConnections={() => {
                            setDagRootNode(node);
                            setShowDag(true);
                        }}/>
                    })}
                </tbody>
            </table>
        </>;
    }

    return <div className="mb-5">
        <ObjectListHeader
            headerIcon={<DashboardIcon compact bgColor={props.headerIconColor} icon={props.headerIcon}/>}
            title={props.title}
            subtitle={sortedNodes.length + ' ' + (props.plural || 'nodes')}
            actionButtonText={props.actionButtonText}
            actionButtonIcon={props.actionButtonIcon}
            actionButtonOnClick={props.actionButtonOnClick}
        >{props.headerChildren}</ObjectListHeader>
        {listContent()}
    </div>
    
}

export default PipelineNodeList;

interface NodeGridProps {
    nodeFilter?: (node: PipelineNode) => boolean;
    searchQuery?: string;
}

const ItemGrid = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    align-items: flex-start;
`

const GridItem = styled.div`
    flex: 0 1 auto;
    min-width: 250px;
    max-width: 600px;
`;

export const NodeGrid = (props: NodeGridProps) => {
    const nodes = usePipelineNodes();
    const navigate = useNavigate();
    const isLoading = nodes.isLoading;

    const filteredNodes = useMemo(() => {
        if (!nodes.data) {
            return [];
        }

        let filtered = nodes.data;

        // Apply custom node filter if provided
        if (props.nodeFilter) {
            filtered = filtered.filter(props.nodeFilter);
        }

        // Apply search query filter if provided
        if (props.searchQuery) {
            const query = props.searchQuery.toLowerCase();
            filtered = filtered.filter(node => 
                node.label.toLowerCase().includes(query)
            );
        }

        // Sort alphabetically by label
        return filtered.sort((a, b) => a.label.localeCompare(b.label));
    }, [nodes.data, props.nodeFilter, props.searchQuery]);

    if (isLoading) {
        return <LoadingCard />;
    }

    return (
        <div className="row g-3">
            {filteredNodes.map(node => (
                <div className="col-4" 
                    key={node.id}
                >
                    <SingleNodeItem
                        node={node}
                        rounded
                        onClick={() => {
                            navigate(`/node/${node.id}`);
                        }}
                    />
                </div>
            ))}
        </div>
    );
};

interface MetricGridProps {
    searchQuery?: string;
}

export const MetricGrid = (props: MetricGridProps) => {
    const metrics = useMeasures();
    const isLoading = metrics.isLoading;

    const filteredMeasures = useMemo(() => {
        if (!metrics.data) {
            return [];
        }

        return metrics.data.filter(m => m.name.toLowerCase().includes(props.searchQuery?.toLowerCase() || '')).sort((a, b) => a.name.localeCompare(b.name));
    }, [metrics.dataUpdatedAt, props.searchQuery]);

    const navigate = useNavigate();
    if (isLoading) {
        return <LoadingCard />;
    }

    return (
        <div className="row g-3">
            {filteredMeasures.map(m => (
                <div className="col-4" key={m.id}>
                    <SingleMeasureItem measure={m} rounded onClick={() => {
                        navigate(`/metrics/${m.id}`);
                    }}/>
                </div>
            ))}
        </div>
    )
}
